import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import "../styles/postDetails.css";

const PostDetails = () => {
  const { id } = useParams();
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const cachedPost = JSON.parse(localStorage.getItem(`post_${id}`));
        if (cachedPost) {
          setPost(cachedPost);
          setLoading(false);
          return;
        }

        const postDoc = await getDoc(doc(db, "posts", id));
        if (postDoc.exists()) {
          const postData = {
            id: postDoc.id,
            ...postDoc.data(),
            createdAt: postDoc.data().createdAt?.toDate() || "No Timestamp",
          };
          setPost(postData);
          localStorage.setItem(`post_${id}`, JSON.stringify(postData));
        } else {
          alert("Post not found!");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching post details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, navigate]);

  const updateCache = (updatedPost) => {
    localStorage.setItem(`post_${id}`, JSON.stringify(updatedPost));
  };

  const handleLikeToggle = async () => {
    if (!user) {
      alert("You need to log in to like a post.");
      return;
    }

    const isLiked = post.likes?.includes(user.uid);
    const updatedLikes = isLiked
      ? post.likes.filter((uid) => uid !== user.uid)
      : [...(post.likes || []), user.uid];
    const updatedLikeCount = isLiked
      ? (post.likeCount || 0) - 1
      : (post.likeCount || 0) + 1;

    const updatedPost = {
      ...post,
      likes: updatedLikes,
      likeCount: updatedLikeCount,
    };

    // Optimistic UI update
    setPost(updatedPost);

    try {
      const postRef = doc(db, "posts", id);
      await updateDoc(postRef, {
        likes: updatedLikes,
        likeCount: updatedLikeCount,
      });
      updateCache(updatedPost);
    } catch (error) {
      console.error("Error toggling like:", error);

      // Revert UI state on failure
      const revertedPost = {
        ...post,
        likes: isLiked
          ? [...(post.likes || []), user.uid]
          : post.likes.filter((uid) => uid !== user.uid),
        likeCount: isLiked
          ? (post.likeCount || 0) + 1
          : (post.likeCount || 0) - 1,
      };
      setPost(revertedPost);
      alert("Failed to update like status. Please try again.");
    }
  };

  const handleShare = () => {
    const shareData = {
      title: post?.title,
      text: post?.content,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch((err) => {
        alert("Error sharing post: " + err.message);
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Post link copied to clipboard!");
    }
  };

  const handleDelete = async () => {
    if (!user || user.uid !== post.userId) {
      alert("You are not authorized to delete this post.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this post?"
    );
    if (!confirmDelete) return;

    try {
      const postRef = doc(db, "posts", id);
      await deleteDoc(postRef);
      localStorage.removeItem(`post_${id}`);
      alert("Post deleted successfully!");
      navigate("/");
    } catch (error) {
      console.error("Error deleting post:", error);
      alert(`Failed to delete post. Error: ${error.message}`);
    }
  };

  if (loading) return <p>Loading post...</p>;

  if (!post) return <p>Post not found.</p>;

  return (
    <div className="post-details-container">
      <div>
        <h1>{post.title}</h1>
        <div
          className="content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        ></div>
        <p>
          <small>
            Posted on:{" "}
            {post.createdAt !== "No Timestamp"
              ? new Date(post.createdAt).toLocaleString()
              : "No Timestamp"}
          </small>
        </p>
        <div className="post-meta">
          <img
            src={post.photoURL || "default-avatar.png"}
            alt={post.displayName || "User"}
            className="post-avatar"
          />
          <p>
            <strong>{post.displayName || "Unknown User"}</strong>
          </p>
          <p>Likes: {post.likeCount || 0}</p>
        </div>
        <button
          onClick={handleLikeToggle}
          className={
            user && post.likes?.includes(user.uid)
              ? "like-button liked"
              : "like-button"
          }
        >
          {user && post.likes?.includes(user.uid) ? "Unlike" : "Like"}
        </button>
        <button onClick={handleShare} className="share-button">
          Share
        </button>
        {user && user.uid === post.userId && (
          <div className="action-buttons">
            <button onClick={handleDelete} className="delete-button">
              Delete Post
            </button>
            <Link to={`/editpost/${id}`} className="edit-button">
              Edit Post
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetails;
